import React, { useEffect, useState } from 'react';
import { HolidayDto, HolidayForm } from '../types/holiday.types';
import { DEFAULT_HOLIDAY_FORM, DOW_LABELS } from '../constants/holiday.constants';
import '../styles/HolidayModal.css';

interface HolidayModalProps {
    isOpen: boolean;
    editTarget: HolidayDto | null;
    onSave: (form: HolidayForm) => Promise<void>;
    onClose: () => void;
}

const HolidayModal: React.FC<HolidayModalProps> = ({ isOpen, editTarget, onSave, onClose }) => {
    const [form, setForm] = useState<HolidayForm>({ ...DEFAULT_HOLIDAY_FORM });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        if (editTarget) {
            setForm({
                seqNo: editTarget.seqNo,
                holidayDate: editTarget.holidayDate,
                holidayName: editTarget.holidayName,
                targetDow: String(editTarget.targetDow),
            });
        } else {
            setForm({ ...DEFAULT_HOLIDAY_FORM });
        }
    }, [isOpen, editTarget]);

    if (!isOpen) return null;

    const setField = (key: keyof HolidayForm, value: string) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        if (!form.holidayDate.trim()) {
            alert('날짜는 필수입니다.');
            return;
        }
        if (!form.holidayName.trim()) {
            alert('공휴일명은 필수입니다.');
            return;
        }
        if (form.targetDow === '') {
            alert('적용 요일을 선택해주세요.');
            return;
        }

        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="holiday-modal-overlay" onClick={handleBackdrop}>
            <div className="holiday-modal" onClick={e => e.stopPropagation()}>
                <div className="holiday-modal-header">
                    <h2 className="holiday-modal-title">
                        {editTarget ? '공휴일 수정' : '공휴일 등록'}
                    </h2>
                </div>

                <div className="holiday-modal-body">
                    <div className="holiday-form-grid">
                        <div className="holiday-form-group">
                            <label className="holiday-form-label required">날짜 (YYYY-MM-DD)</label>
                            <input
                                className="holiday-form-input"
                                type="date"
                                value={form.holidayDate}
                                onChange={e => setField('holidayDate', e.target.value)}
                            />
                        </div>

                        <div className="holiday-form-group">
                            <label className="holiday-form-label required">공휴일명</label>
                            <input
                                className="holiday-form-input"
                                type="text"
                                placeholder="예) 신정"
                                value={form.holidayName}
                                onChange={e => setField('holidayName', e.target.value)}
                            />
                        </div>

                        <div className="holiday-form-group">
                            <label className="holiday-form-label required">적용 요일 (슬롯 목표 기준)</label>
                            <select
                                className="holiday-form-select"
                                value={form.targetDow}
                                onChange={e => setField('targetDow', e.target.value)}
                            >
                                {DOW_LABELS.map((label, idx) => (
                                    <option key={idx + 1} value={idx + 1}>{label}</option>
                                ))}
                            </select>
                            <span className="holiday-form-hint">
                                이 공휴일에 적용할 목표 건수 기준 요일 (보통 토=5 또는 일=6)
                            </span>
                        </div>
                    </div>
                </div>

                <div className="holiday-modal-footer">
                    <button className="holiday-btn-cancel" type="button" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="holiday-btn-save"
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HolidayModal;
